import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { ITableColumn } from 'src/components/Table/types';
import { CsvDateFormat } from 'src/services/client-types';
import {
  useDownloadCSVData,
  UseDownloadCSVDataReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/useDownloadCSVData';
import {
  useDownloadPDF,
  UseDownloadPDFReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadPDF/useDownloadPDF';
import {
  useCommonAreaServiceRequestsApi,
  UseCommonAreaServiceRequestsApiReturn,
} from './useCommonAreaServiceRequestsApi/useCommonAreaServiceRequestsApi';
import {
  useCommonAreaServiceRequestsFormState,
  UseCommonAreaServiceRequestsFormStateReturn,
} from './useCommonAreaServiceRequestsFormState';
import {
  usePerPageServiceRequestTableState,
  UsePerPageServiceRequestTableStateReturn,
  PerPageServiceRequestLightboxProps,
} from 'src/pages/ServiceRequests/CustomHooks/usePerPageServiceRequestTableState';

export interface UseCommonAreaServiceRequestsOrchestratorArgs {
  commonAreaId: string;
  navigateTo: string;
  navLabel: string;
}

export interface UseCommonAreaServiceRequestsOrchestratorReturn {
  serviceRequests: IServiceRequest[];
  visibleServiceRequests: IServiceRequest[];
  isLoading: boolean;
  refetch: () => Promise<void>;

  searchQuery: string;
  setSearchQuery: (query: string) => void;

  showResolvedOnly: boolean;
  toggleShowResolved: () => void;

  selectedIds: string[];
  allVisibleSelected: boolean;
  idsForCsvExport: string[];
  toggleSelection: (id: string) => void;
  toggleSelectionBatch: (ids: string[]) => void;
  toggleSelectAllVisible: () => void;
  clearSelection: () => void;

  expandedGroupIds: string[];
  toggleGroupExpansion: (id: string) => void;

  isDownloadOverlayOpen: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  onDownloadCSV: (dateFormat: CsvDateFormat) => Promise<boolean>;
  onDownloadPDF: () => Promise<boolean>;
  isCSVLoading: boolean;
  isPDFLoading: boolean;

  columns: ITableColumn[];
  skeletonColumns: ITableColumn[];
  skeletonData: any[];
  expandedGroups: any[];
  lightboxProps: PerPageServiceRequestLightboxProps;

  reset: () => void;
}

export const useCommonAreaServiceRequestsOrchestrator = ({
  commonAreaId,
  navigateTo,
  navLabel,
}: UseCommonAreaServiceRequestsOrchestratorArgs): UseCommonAreaServiceRequestsOrchestratorReturn => {
  const user: any = useSelector(selectAuthenticatedUser);
  const userId: string = user?.id ?? '';
  const role: string = user?.roles?.[0]?.role?.name ?? '';

  const api: UseCommonAreaServiceRequestsApiReturn =
    useCommonAreaServiceRequestsApi();

  const serviceRequests: IServiceRequest[] = api.serviceRequests;
  const isLoading: boolean = api.loading;

  const formState: UseCommonAreaServiceRequestsFormStateReturn =
    useCommonAreaServiceRequestsFormState({ serviceRequests });

  const tableState: UsePerPageServiceRequestTableStateReturn =
    usePerPageServiceRequestTableState({
      visibleServiceRequests: formState.visibleServiceRequests,
      selectedIds: formState.selectedIds,
      allVisibleSelected: formState.allVisibleSelected,
      toggleSelection: formState.toggleSelection,
      toggleSelectionBatch: formState.toggleSelectionBatch,
      toggleSelectAllVisible: formState.toggleSelectAllVisible,
      navigateTo,
      navLabel,
    });

  const csvDownload: UseDownloadCSVDataReturn = useDownloadCSVData();
  const pdfDownload: UseDownloadPDFReturn = useDownloadPDF();

  const onDownloadCSV = async (dateFormat: CsvDateFormat): Promise<boolean> => {
    return await csvDownload.onDownloadCSV(formState.idsForCsvExport, dateFormat);
  };

  const onDownloadPDF = async (): Promise<boolean> => {
    return await pdfDownload.onDownloadPDF(
      userId,
      formState.idsForCsvExport.join('&')
    );
  };

  const refetch = async (): Promise<void> => {
    formState.clearSelection();
    await api.fetchCommonAreaServiceRequests({ commonAreaId, role });
  };

  useEffect(() => {
    if (commonAreaId && role) {
      void refetch();
    }
  }, [commonAreaId, role]);

  const reset = (): void => {
    formState.reset();
  };

  return {
    serviceRequests,
    visibleServiceRequests: formState.visibleServiceRequests,
    isLoading,
    refetch,

    searchQuery: formState.searchQuery,
    setSearchQuery: formState.setSearchQuery,

    showResolvedOnly: formState.showResolvedOnly,
    toggleShowResolved: formState.toggleShowResolved,

    selectedIds: formState.selectedIds,
    allVisibleSelected: formState.allVisibleSelected,
    idsForCsvExport: formState.idsForCsvExport,
    toggleSelection: formState.toggleSelection,
    toggleSelectionBatch: formState.toggleSelectionBatch,
    toggleSelectAllVisible: formState.toggleSelectAllVisible,
    clearSelection: formState.clearSelection,

    expandedGroupIds: formState.expandedGroupIds,
    toggleGroupExpansion: formState.toggleGroupExpansion,

    isDownloadOverlayOpen: formState.isDownloadOverlayOpen,
    openDownloadOverlay: formState.openDownloadOverlay,
    closeDownloadOverlay: formState.closeDownloadOverlay,

    onDownloadCSV,
    onDownloadPDF,
    isCSVLoading: csvDownload.isLoading,
    isPDFLoading: pdfDownload.isLoading,

    columns: tableState.columns,
    skeletonColumns: tableState.skeletonColumns,
    skeletonData: tableState.skeletonData,
    expandedGroups: tableState.expandedGroups,
    lightboxProps: tableState.lightboxProps,

    reset,
  };
};
